<template>
  <div class="aniversariantes-page">
    <section class="content">
      <PageHeader
        :title="t('Aniversariantes')"
        :subtitle="t('Gerencie as mensagens de aniversário dos clientes')"
        icon="pi pi-bell"
      />

      <!-- Stats Cards -->
      <div class="stats-cards">
        <div class="stat-card total" data-testid="stat-total">
          <div class="stat-icon">
            <i class="pi pi-users"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ totalAniversariantes }}</div>
            <div class="stat-label">{{ t('Aniversariantes') }}</div>
          </div>
          <div class="stat-trend">
            <i class="pi pi-calendar"></i>
          </div>
        </div>

        <div class="stat-card enviados" data-testid="stat-enviados">
          <div class="stat-icon success">
            <i class="pi pi-check-circle"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ totalEnviados }}</div>
            <div class="stat-label">{{ t('Enviados') }}</div>
          </div>
          <div class="stat-badge success">
            <i class="pi pi-whatsapp"></i>
          </div>
        </div>

        <div class="stat-card pendentes" data-testid="stat-pendentes">
          <div class="stat-icon warning">
            <i class="pi pi-clock"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ totalPendentes }}</div>
            <div class="stat-label">{{ t('Pendentes') }}</div>
          </div>
          <div class="stat-badge warning">
            <i class="pi pi-exclamation-circle"></i>
          </div>
        </div>

        <div class="stat-card idade-media" data-testid="stat-idade-media">
          <div class="stat-icon info">
            <i class="pi pi-chart-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ idadeMedia }}</div>
            <div class="stat-label">{{ t('Idade Média') }}</div>
          </div>
          <div class="stat-trend">
            <i class="pi pi-user"></i>
          </div>
        </div>
      </div>

      <!-- Toolbar: only the bulk-send action. Filtering happens via the global
           filter (useFiltro) for date scope and the DataTable column filter
           (Enviado/Pendente) for per-row status. -->
      <div class="page-toolbar">
        <Button
          v-if="hasPendentes"
          data-testid="aniversariantes-batch-send-button"
          :label="t('Enviar Todos')"
          :badge="totalPendentes.toString()"
          icon="pi pi-whatsapp"
          severity="success"
          size="small"
          class="batch-send-btn"
          @click="handleEnviarLote"
        />
      </div>

      <!-- Table Section -->
      <div class="table-section">
        <Box variant="primary" :padding="false">
          <AniversariantesTable
            :aniversariantes="aniversariantes"
            :loading="isLoading"
            :franquiaId="franquiaId"
            @refresh="loadData"
            @send-whatsapp="handleSendWhatsApp"
          />
        </Box>
      </div>

    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from '@/ui/AppButton.vue';
import Box from '@/ui/Box.vue';
import PageHeader from '@/ui/PageHeader.vue';
import AniversariantesTable from './AniversariantesTable.vue';
import { useAniversariantes } from '@/composables/useAniversariantes';
import { useFiltro } from '@/composables/useFiltro';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface Aniversariante {
  cliente_id: Id;
  cliente_nome?: string;
  enviado?: boolean | number;
  idade?: number | string;
  [key: string]: unknown;
}

const { t } = useI18n();

const { fetchAniversariantes } = useAniversariantes();
const { getDateRange, init: initFiltro } = useFiltro();

const isLoading = ref<boolean>(false);
const aniversariantes = ref<Aniversariante[]>([]);
const franquiaId = ref<Id | null>(null);

const totalAniversariantes = computed<number>(() => aniversariantes.value.length);
const totalEnviados = computed<number>(() => aniversariantes.value.filter(a => a.enviado).length);
const totalPendentes = computed<number>(() => aniversariantes.value.filter(a => !a.enviado).length);
const hasPendentes = computed<boolean>(() => totalPendentes.value > 0);

const idadeMedia = computed<string>(() => {
  if (aniversariantes.value.length === 0) return '-';

  const somaIdades = aniversariantes.value.reduce((acc, a) => {
    const idade = parseInt(String(a.idade ?? '')) || 0;
    return acc + idade;
  }, 0);

  const media = Math.round(somaIdades / aniversariantes.value.length);
  return media + ' ' + (media === 1 ? t('ano') : t('anos'));
});

const loadData = async (): Promise<void> => {
  log('AniversariantesPage', 'loadData', 'Loading scoped by global filter');

  if (!franquiaId.value) {
    log('AniversariantesPage', 'loadData', 'No franquia selected');
    return;
  }

  isLoading.value = true;
  try {
    const { startDate, endDate } = getDateRange();
    const data = (await fetchAniversariantes(startDate || undefined, endDate || undefined)) as unknown as Aniversariante[];
    aniversariantes.value = data || [];
    log('AniversariantesPage', 'loadData', 'Loaded', aniversariantes.value.length);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('AniversariantesPage', 'loadData', 'Error', message);
  } finally {
    isLoading.value = false;
  }
};

const handleSendWhatsApp = (aniversariante: Aniversariante): void => {
  log('AniversariantesPage', 'handleSendWhatsApp', 'Sending WhatsApp', aniversariante);

  eventBus.emit('modal.aniversariante-preview.open', {
    aniversariante: aniversariante,
    franquiaId: franquiaId.value
  });
};

const handleEnviarLote = (): void => {
  const pendentes = aniversariantes.value.filter(a => !a.enviado);
  log('AniversariantesPage', 'handleEnviarLote', 'Pendentes:', pendentes.length);

  if (pendentes.length === 0) return;

  eventBus.emit('modal.aniversariante-lote.open', {
    aniversariantes: pendentes,
    franquiaId: franquiaId.value
  });
};

const handleRefresh = (): void => {
  log('AniversariantesPage', 'handleRefresh', 'Refresh event received');
  loadData();
};

const handleFilterUpdated = (): void => {
  log('AniversariantesPage', 'handleFilterUpdated', 'Global filter changed, reloading');
  loadData();
};

onMounted(async () => {
  log('AniversariantesPage', 'onMounted', 'Iniciando página');

  await initFiltro();

  const franquias = permissionsService.getFranquiasSelectArray();
  if (franquias && franquias.length > 0) {
    franquiaId.value = franquias[0].ID;
  }

  await loadData();

  eventBus.on('aniversariantes.refresh', handleRefresh);
  eventBus.on('filter.updated', handleFilterUpdated);
});

onUnmounted(() => {
  eventBus.off('aniversariantes.refresh', handleRefresh);
  eventBus.off('filter.updated', handleFilterUpdated);
});
</script>

<style scoped>
.aniversariantes-page {
  padding: 24px;
  min-height: 100vh;
  background: var(--surface-ground);
}

.content {
  max-width: 1400px;
  margin: 0 auto;
}

/* Stats Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--surface-border);
  transition: background 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.stat-card.total::before {
  background: linear-gradient(90deg, var(--primary-400), var(--primary-600));
}

.stat-card.enviados::before {
  background: linear-gradient(90deg, var(--green-400), var(--green-600));
}

.stat-card.pendentes::before {
  background: linear-gradient(90deg, var(--orange-400), var(--orange-600));
}

.stat-card.idade-media::before {
  background: linear-gradient(90deg, var(--cyan-400), var(--cyan-600));
}

.stat-icon {
  width: 56px;
  height: 56px;
  background: var(--surface-100);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon i {
  font-size: 26px;
  color: var(--text-color-secondary);
}

.stat-icon.success {
  background: var(--green-50);
}

.stat-icon.success i {
  color: var(--green-500);
}

.stat-icon.warning {
  background: var(--orange-50);
}

.stat-icon.warning i {
  color: var(--orange-500);
}

.stat-icon.info {
  background: var(--cyan-50);
}

.stat-icon.info i {
  color: var(--cyan-500);
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
  color: var(--text-color);
}

.stat-label {
  font-size: 13px;
  color: var(--text-color-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-trend {
  font-size: 20px;
  color: var(--text-color-secondary);
  opacity: 0.5;
}

.stat-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-badge.success {
  background: var(--green-100);
}

.stat-badge.success i {
  color: var(--green-600);
  font-size: 16px;
}

.stat-badge.warning {
  background: var(--orange-100);
}

.stat-badge.warning i {
  color: var(--orange-600);
  font-size: 16px;
}

/* Page toolbar (bulk-send action only) */
.page-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.batch-send-btn {
  border-radius: 8px;
  font-weight: 600;
  padding: 8px 20px;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  transition: all 0.3s ease;
}

.batch-send-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(34, 197, 94, 0.4);
}

/* Table Section */
.table-section {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

/* Responsive */
@media (max-width: 1200px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .aniversariantes-page {
    padding: 16px;
  }

  .stats-cards {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-value {
    font-size: 26px;
  }

  .batch-send-btn {
    width: 100%;
  }
}
</style>
